<template>
  <div class="fc-notification-form">
    <NotificationConfigureHeader
      class="fc-notification-form__span-2"
      title="Escalation"
      subtitle="Email a lead when tasks breach their due-date SLA."
      kind="template"
      icon="mdi-alert-octagon-outline"
    />
    <NotificationNameField
      class="fc-notification-form__span-2"
      :value="name"
      :auto-name="autoName"
      :is-custom="nameIsCustom"
      :error="nameError"
      @input="name = $event"
      @update:isCustom="nameIsCustom = $event"
      @reset="resetName"
    />
    <p class="fc-notification-form__hint fc-notification-form__span-2">
      {{ nameHint }}
    </p>
    <NotificationEnableRow
      class="fc-notification-form__span-2"
      v-model="flag"
      :title="flagLabel"
      hint="Added to every new cycle while this is on."
      :disabled="disabled"
    />
    <div class="fc-notification-form__pair">
      <div class="fc-notification-form__pair-cell fc-notification-form__pair-cell--chip-wrap">
        <span class="fc-notification-form__label">Locations</span>
        <ChipMultiSelect
          v-model="selectedLocations"
          :items="locations"
          item-text="name"
          item-value="_id"
          outlined
          :error="locationsError"
          :disabled="disabled"
          data-testid="escalation-template-form__locations"
          data-testid-prefix="escalation-template-form__locations"
        />
      </div>
      <div class="fc-notification-form__pair-cell">
        <span class="fc-notification-form__label">Recipients</span>
        <v-text-field
          v-model="emails"
          outlined
          dense
          hide-details="auto"
          placeholder="Separate multiple emails with commas"
          :error-messages="recipientsError ? [recipientsError] : []"
          :disabled="disabled"
          data-testid="escalation-template-form__recipients"
        />
      </div>
    </div>
    <div class="fc-notification-form__field fc-notification-form__span-2">
      <span class="fc-notification-form__label">Email subject</span>
      <v-text-field
        v-model="subject"
        outlined
        dense
        hide-details="auto"
        :error-messages="subjectError ? [subjectError] : []"
        :disabled="disabled"
        data-testid="escalation-template-form__subject"
      />
    </div>
    <div class="fc-notification-form__field fc-notification-form__span-2">
      <span class="fc-notification-form__label">Email body</span>
      <v-textarea
        v-model="mailBody"
        outlined
        dense
        hide-details="auto"
        rows="3"
        :error-messages="bodyError ? [bodyError] : []"
        :disabled="disabled"
        data-testid="escalation-template-form__body"
      />
    </div>
    <div class="fc-notification-form__field fc-notification-form__span-2 fc-notification-form__field--chip-wrap">
      <span class="fc-notification-form__label">Category</span>
      <ChipMultiSelect
        v-model="category"
        :items="categories"
        item-text="name"
        item-value="id"
        outlined
        :error="categoryError"
        :disabled="disabled"
        data-testid="escalation-template-form__category"
        data-testid-prefix="escalation-template-form__category"
      />
    </div>
    <div class="fc-notification-form__field">
      <span class="fc-notification-form__label">SLA</span>
      <v-select
        v-model="sla"
        :items="slaList"
        item-text="name"
        item-value="id"
        outlined
        dense
        hide-details="auto"
        :error-messages="slaError ? [slaError] : []"
        :disabled="disabled"
        data-testid="escalation-template-form__sla"
      />
      <p class="fc-notification-form__hint">Each option is how long after the due date the email is sent.</p>
    </div>
    <div class="fc-notification-form__field">
      <span class="fc-notification-form__label">Priority</span>
      <v-select
        v-model="priority"
        :items="priorityList"
        item-text="name"
        item-value="id"
        outlined
        dense
        hide-details="auto"
        disabled
        data-testid="escalation-template-form__priority"
      />
    </div>
    <div class="fc-notification-form__field">
      <ScheduledAtField
        v-model="startTime"
        label="Checks at"
        compact-timezone
        :error="scheduleError"
        :disabled="disabled"
        data-testid="escalation-template-form__checks-at"
      />
    </div>
    <div class="fc-notification-form__field">
      <span class="fc-notification-form__label">Active for (days after period end)</span>
      <v-text-field
        v-model="endDays"
        type="number"
        outlined
        dense
        hide-details="auto"
        :error-messages="endDaysError ? [endDaysError] : []"
        :disabled="disabled"
        data-testid="escalation-template-form__end-days"
      />
    </div>
    <div class="fc-notification-infobox fc-notification-form__span-2">
      <v-icon small>mdi-information-outline</v-icon>
      <p>
        Edits here apply to cycles created from now on. A running cycle keeps its own copy in
        <b>Cycle Settings › Automations</b>.
      </p>
    </div>
  </div>
</template>

<script>
import ChipMultiSelect from '../../../../components/ChipMultiSelect/ChipMultiSelect.vue';
import {
  NotificationConfigureHeader,
  NotificationEnableRow,
  NotificationNameField,
  ScheduledAtField,
} from '../../../../components/Notifications';
import { ESCALATION_SLA_OPTIONS, NOTIFICATION_FORM_COPY, NOTIFICATION_TYPE_KEYS } from '../../../../utils/constants';
import { buildNotificationName } from '../../../../utils/notificationNameUtils';
import { localStartTimeFromNotification } from '../../../../utils/notificationScheduleUtils';
import { formatEmailList, parseEmailList } from '../../../../utils/validationUtils';
import notificationFormErrorMixin from './notificationFormErrorMixin';

function ensureArray(value) {
  if (Array.isArray(value)) return [...value];
  if (value === undefined || value === null || value === '') return [];
  return [value];
}

export default {
  name: 'EscalationTemplateForm',
  components: {
    ChipMultiSelect,
    NotificationConfigureHeader,
    NotificationEnableRow,
    NotificationNameField,
    ScheduledAtField,
  },
  mixins: [notificationFormErrorMixin],
  props: {
    value: { type: Object, default: () => ({}) },
    locations: { type: Array, default: () => [] },
    serverErrors: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
    flagLabel: { type: String, default: 'Auto-apply' },
  },
  data() {
    return {
      name: this.value.name || '',
      nameIsCustom: this.value.name_is_custom === true,
      selectedLocations: [...(this.value.meta?.location_key || this.value.meta?.locations || [])],
      emails: formatEmailList(this.value.meta?.toUsers),
      subject: this.value.subject || '',
      mailBody: this.value.mailBody || '',
      category: ensureArray(this.value.meta?.category),
      sla: this.value.meta?.sla ?? this.value.sla ?? '',
      priority: this.value.meta?.priority || this.value.priority || 'High',
      startTime: localStartTimeFromNotification(this.value),
      endDays: this.value.meta?.endDays ?? this.value.end_days ?? '',
      flag: this.value.flag !== false,
      nameHint: NOTIFICATION_FORM_COPY.NAME_HINT,
      slaList: ESCALATION_SLA_OPTIONS,
      priorityList: [{ name: 'High', id: 'High' }],
    };
  },
  computed: {
    categories() {
      return this.$store.getters['emailNotifications/fetchCategories'] || [];
    },
    autoName() {
      return buildNotificationName(
        NOTIFICATION_TYPE_KEYS.TASK_LIST_ESCALATION,
        { locations: this.selectedLocations },
        this.locations
      );
    },
  },
  methods: {
    resetName() {
      this.nameIsCustom = false;
      this.name = this.autoName;
    },
    toPayload() {
      return {
        featureDetails: {
          _id: this.value._id,
          feature: NOTIFICATION_TYPE_KEYS.TASK_LIST_ESCALATION,
          type: 'fbc',
          flag: this.flag,
          name: this.nameIsCustom ? this.name : this.autoName,
          name_is_custom: this.nameIsCustom,
          subject: this.subject,
          mailBody: this.mailBody,
          meta: {
            location_key: this.selectedLocations,
            toUsers: parseEmailList(this.emails),
            category: this.category,
            sla: this.sla,
            priority: this.priority,
            schedule: { startTime: this.startTime },
            endDays: this.endDays === '' || this.endDays == null ? '' : Number(this.endDays),
          },
        },
      };
    },
  },
};
</script>
